/**
 * Converts an object using mapping dictionaries.
 *
 * @template T - The type of the source data object.
 * @template D - The type of the mapping dictionary.
 * @template I - The type of the reverse mapping dictionary.
 * @template S - The type of the resulting data object.
 *
 * @param {T} params - The data object to convert.
 * @param {D} dictionary - The mapping dictionary.
 * @param {I} reverseDictionary - The reverse mapping dictionary.
 * @returns {S} Returns the converted data object.
 */
export function convertObjectByDictionaries<
  T extends object,
  D extends Record<string, keyof I | undefined>,
  I extends Record<string, keyof D | undefined>,
  S extends object,
>(params: T, dictionary: D, reverseDictionary: I): S {
  const result: Record<string, unknown> = {};
  const record = params as Record<string, unknown>;
  for (const key in record) {
    if (!Object.prototype.hasOwnProperty.call(record, key)) {
      continue;
    }
    const value = record[key];
    if (value === null || value === undefined) {
      continue;
    }
    const current = dictionary[key];
    if (current) {
      result[current as string] = value;
    } else if (reverseDictionary[key] !== undefined) {
      result[key] = value;
    }
  }
  return result as S;
}

/**
 * Safely parses a JSON string.
 *
 * @param text - The JSON string to parse.
 * @returns The parsed object if valid, otherwise `null`.
 */
export const parseJson = <T extends object = object>(
  text: string
): T | null => {
  try {
    return JSON.parse(text) as T;
  } catch (_error) {
    return null;
  }
};
